/**
 * Client-side mirror of the backend's interest rate chart rules.
 *
 * A chart is required for a Savings Deposit product detail and rejected for
 * any other deposit type. The rules below are transcribed from
 * `InterestRateChartValidator.cs` in `advanclyservice`, deliberately including
 * its quirks — the point is that the form never posts a chart the server will
 * reject, and that the message it shows is the message the server would have
 * shown.
 *
 * Slabs are validated **in array order**. There is no sort on either side, so
 * the order the admin arranges them in is the order that is judged.
 *
 * See `docs/ADMIN_BACKEND_QUESTIONS.md` Q2 and Q3 for the two behaviours here
 * that look like backend bugs and are mirrored anyway.
 */

/** `Enums.DepositPeriodFrequency` — the unit a slab's period is counted in. */
export const PeriodType = {
  Days: 0,
  Weeks: 1,
  Months: 2,
  Years: 3,
} as const;

export type TPeriodType = (typeof PeriodType)[keyof typeof PeriodType];

export type TChartSlab = {
  id?: number | null;
  fromAmount?: number | null;
  toAmount?: number | null;
  fromPeriod?: number | null;
  toPeriod?: number | null;
  periodType?: number | null;
  interestRate: number;
  description?: string | null;
};

export type TInterestRateChart = {
  id?: number | null;
  fromDate?: string | null;
  toDate?: string | null;
  name?: string | null;
  description?: string | null;
  isPrimaryGroupingByAmount: boolean;
  chartSlabs: TChartSlab[];
};

/** One problem, addressed to the row and field that can fix it. */
export type TChartError = {
  message: string;
  /** Index into `chartSlabs`, when the problem belongs to a row. */
  slabIndex?: number;
  /** The row's field to mark, when the problem belongs to one. */
  field?: keyof TChartSlab;
};

const MAX_NAME = 100;
const MAX_DESCRIPTION = 200;
const MIN_RATE = 0;
const MAX_RATE = 100;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Every problem with a chart — header and slabs. Empty means it will post. */
export function validateChart(chart: TInterestRateChart): TChartError[] {
  return [
    ...validateChartHeader(chart),
    ...validateChartSlabs(chart.chartSlabs, chart.isPrimaryGroupingByAmount),
  ];
}

export function validateChartHeader(chart: TInterestRateChart): TChartError[] {
  const errors: TChartError[] = [];

  if (!chart.fromDate) {
    errors.push({ message: "Interest rate chart from date is required" });
  }

  if (
    chart.fromDate &&
    chart.toDate &&
    new Date(chart.toDate) < new Date(chart.fromDate)
  ) {
    errors.push({
      message: "Interest rate chart to date cannot be earlier than from date",
    });
  }

  if (!chart.name?.trim()) {
    errors.push({ message: "Interest rate chart name is required" });
  }

  if (chart.name && chart.name.length > MAX_NAME) {
    errors.push({
      message: `Interest rate chart name cannot exceed ${MAX_NAME} characters`,
    });
  }

  if (chart.description && chart.description.length > MAX_DESCRIPTION) {
    errors.push({
      message: `Interest rate chart description cannot exceed ${MAX_DESCRIPTION} characters`,
    });
  }

  return errors;
}

/**
 * The slab rules, in the backend's own order so the first error matches.
 */
export function validateChartSlabs(
  slabs: TChartSlab[],
  isPrimaryGroupingByAmount: boolean,
): TChartError[] {
  if (!slabs?.length) {
    return [{ message: "Interest rate chart slab is required" }];
  }

  const errors: TChartError[] = [];
  let periodType: number | null | undefined;

  slabs.forEach((slab, index) => {
    if (slab.interestRate < MIN_RATE || slab.interestRate > MAX_RATE) {
      errors.push({
        message:
          "Interest rate chart slab interest rate must be between 0 and 100",
        slabIndex: index,
        field: "interestRate",
      });
    }

    if (
      slab.periodType !== null &&
      slab.periodType !== undefined &&
      !isPeriodType(slab.periodType)
    ) {
      errors.push({
        message: "Invalid period type",
        slabIndex: index,
        field: "periodType",
      });
    }

    if (!hasPrimaryAxis(isPrimaryGroupingByAmount, slab)) {
      errors.push({
        message: `Chart slab at position ${index + 1} '${
          isPrimaryGroupingByAmount ? "From Amount" : "From Period"
        }' is required.`,
        slabIndex: index,
        field: isPrimaryGroupingByAmount ? "fromAmount" : "fromPeriod",
      });

      return;
    }

    if (index === 0) {
      if (isNotProperStart(slab)) {
        errors.push({
          message: "Chart slab range start is incorrect",
          slabIndex: index,
          field: isPrimaryGroupingByAmount ? "fromAmount" : "fromPeriod",
        });
      }

      periodType = slab.periodType;
    } else if (
      slab.periodType !== null &&
      slab.periodType !== undefined &&
      periodType !== slab.periodType
    ) {
      errors.push({
        message: "period type is not the same",
        slabIndex: index,
        field: "periodType",
      });
    }

    /*
     * A band that ends below where it starts.
     *
     * The editor derives every end from the next band's start, so typing a
     * start below the previous one inverts that band rather than overlapping
     * anything — `100,001 – 49,999`. The pairwise checks below compare a band
     * to its neighbour and see nothing wrong with it, so it reached the review
     * and then the server unremarked.
     */
    if (endsBeforeItStarts(slab, isPrimaryGroupingByAmount)) {
      errors.push({
        message: `Chart slab at position ${index + 1} starts after it ends`,
        slabIndex: index,
        field: isPrimaryGroupingByAmount ? "fromAmount" : "fromPeriod",
      });
    }

    const next = slabs[index + 1];

    if (next) {
      // The next iteration reports its own missing axis; comparing against it
      // here would produce a second, less useful message.
      if (!hasPrimaryAxis(isPrimaryGroupingByAmount, next)) {
        return;
      }

      if (isOverlapping(slab, next, isPrimaryGroupingByAmount)) {
        errors.push({
          message: `Interest rate chart slab range overlapping for positions ${
            index + 1
          } and ${index + 2}`,
          slabIndex: index + 1,
          field: isPrimaryGroupingByAmount ? "fromAmount" : "fromPeriod",
        });
      }

      if (hasGap(slab, next, isPrimaryGroupingByAmount)) {
        errors.push({
          message: `Interest rate chart slab has gaps for positions ${
            index + 1
          } and ${index + 2}`,
          slabIndex: index + 1,
          field: isPrimaryGroupingByAmount ? "fromAmount" : "fromPeriod",
        });
      }

      return;
    }

    if (isNotProperEnd(slab)) {
      errors.push({
        message: "Interest rate chart slab range end incorrect.",
        slabIndex: index,
        field: isPrimaryGroupingByAmount ? "toAmount" : "toPeriod",
      });
    }
  });

  return errors;
}

function endsBeforeItStarts(
  slab: TChartSlab,
  isPrimaryGroupingByAmount: boolean,
): boolean {
  const start = isPrimaryGroupingByAmount ? slab.fromAmount : slab.fromPeriod;
  const end = isPrimaryGroupingByAmount ? slab.toAmount : slab.toPeriod;

  if (
    start === null ||
    start === undefined ||
    end === null ||
    end === undefined
  ) {
    return false;
  }

  return end < start;
}

// ---------------------------------------------------------------------------
// Editing helpers — keep the shape valid as it is built
// ---------------------------------------------------------------------------

/**
 * Where each band ends, given where the next one starts.
 *
 * Only the start of a band is ever edited. Every band's end is `next.start - 1`
 * and the last band's end is open, which makes a gap or an overlap impossible
 * to express rather than merely possible to detect. That matters because the
 * backend's contiguity rule is `prev.to + 1 === next.from`, which is easy to
 * get wrong by hand and produces an error message a long way from the cause.
 */
export function withDerivedEnds(
  slabs: TChartSlab[],
  isPrimaryGroupingByAmount: boolean,
): TChartSlab[] {
  const start = isPrimaryGroupingByAmount ? "fromAmount" : "fromPeriod";
  const end = isPrimaryGroupingByAmount ? "toAmount" : "toPeriod";

  return slabs.map((slab, index) => {
    const next = slabs[index + 1];
    const nextStart = next?.[start];

    return {
      ...slab,
      [end]: isSet(nextStart) ? Number(nextStart) - 1 : null,
    };
  });
}

/** Sets a band's start, and re-derives the ends around it. */
export function setSlabStart(
  slabs: TChartSlab[],
  index: number,
  value: number | null,
  isPrimaryGroupingByAmount: boolean,
): TChartSlab[] {
  const start = isPrimaryGroupingByAmount ? "fromAmount" : "fromPeriod";

  return withDerivedEnds(
    slabs.map((slab, i) => (i === index ? { ...slab, [start]: value } : slab)),
    isPrimaryGroupingByAmount,
  );
}

/**
 * A new band, opening one above where the previous one starts.
 *
 * One above the previous *start*, not its end, because the end is derived from
 * this value rather than the other way round. The admin then types the real
 * threshold.
 */
export function appendChartSlab(
  slabs: TChartSlab[],
  isPrimaryGroupingByAmount: boolean,
  rate = 0,
): TChartSlab[] {
  const start = isPrimaryGroupingByAmount ? "fromAmount" : "fromPeriod";

  const blank: TChartSlab = {
    fromAmount: isPrimaryGroupingByAmount ? 0 : null,
    toAmount: null,
    fromPeriod: isPrimaryGroupingByAmount ? 1 : 0,
    toPeriod: null,
    periodType: PeriodType.Days,
    interestRate: rate,
    description: "",
  };

  if (!slabs.length) {
    return [blank];
  }

  const previous = slabs[slabs.length - 1];
  const previousStart = previous[start];

  return withDerivedEnds(
    [
      ...slabs,
      {
        ...blank,
        periodType: previous.periodType ?? PeriodType.Days,
        [start]: isSet(previousStart) ? Number(previousStart) + 1 : undefined,
      },
    ],
    isPrimaryGroupingByAmount,
  );
}

/**
 * Removes a band and closes the gap it leaves.
 *
 * The band before the hole takes over the end of the band being removed, so
 * the range stays contiguous and the last band stays open-ended.
 */
export function removeChartSlab(
  slabs: TChartSlab[],
  index: number,
  isPrimaryGroupingByAmount: boolean,
): TChartSlab[] {
  // The ends re-derive from the starts that remain, so the range closes over
  // the hole on its own.
  return withDerivedEnds(
    slabs.filter((_slab, i) => i !== index),
    isPrimaryGroupingByAmount,
  );
}

// ---------------------------------------------------------------------------
// The backend's predicates, transcribed
// ---------------------------------------------------------------------------

function isPeriodType(value: number): value is TPeriodType {
  return (Object.values(PeriodType) as number[]).includes(value);
}

function hasPrimaryAxis(byAmount: boolean, slab: TChartSlab): boolean {
  return byAmount ? isSet(slab.fromAmount) : isSet(slab.fromPeriod);
}

function isSet(value: number | null | undefined): value is number {
  return value !== null && value !== undefined;
}

/** A range starts at 0 or 1. Checked on whichever axes are supplied. */
function isNotProperAmountStart(slab: TChartSlab): boolean {
  return (
    isSet(slab.fromAmount) && slab.fromAmount !== 1 && slab.fromAmount !== 0
  );
}

function isNotProperPeriodStart(slab: TChartSlab): boolean {
  return (
    isSet(slab.fromPeriod) && slab.fromPeriod !== 1 && slab.fromPeriod !== 0
  );
}

function isNotProperStart(slab: TChartSlab): boolean {
  return isNotProperAmountStart(slab) || isNotProperPeriodStart(slab);
}

/** Bands touch at `prev.to + 1`. An open-ended `prev` cannot be followed. */
function amountDoesNotFollow(
  prev: TChartSlab,
  nextFrom?: number | null,
): boolean {
  return (
    !isSet(prev.toAmount) || (isSet(nextFrom) && prev.toAmount + 1 !== nextFrom)
  );
}

function periodDoesNotFollow(
  prev: TChartSlab,
  nextFrom?: number | null,
): boolean {
  return (
    !isSet(prev.toPeriod) || (isSet(nextFrom) && prev.toPeriod + 1 !== nextFrom)
  );
}

/** Only the last band may be open-ended. */
function isNotProperEnd(slab: TChartSlab): boolean {
  return !(!isSet(slab.toPeriod) || !isSet(slab.toAmount));
}

function sameNumber(a?: number | null, b?: number | null): boolean {
  return (!isSet(a) && !isSet(b)) || a === b;
}

function isAmountSame(a: TChartSlab, b: TChartSlab): boolean {
  return (
    sameNumber(a.fromAmount, b.fromAmount) && sameNumber(a.toAmount, b.toAmount)
  );
}

function isPeriodSame(a: TChartSlab, b: TChartSlab): boolean {
  return (
    sameNumber(a.fromPeriod, b.fromPeriod) && sameNumber(a.toPeriod, b.toPeriod)
  );
}

function isPeriodOverlapping(first: TChartSlab, second: TChartSlab): boolean {
  if (sameNumber(first.fromPeriod, second.fromPeriod)) return true;
  if (sameNumber(first.toPeriod, second.toPeriod)) return true;
  if (!isSet(first.toPeriod)) return true;
  if (!isSet(second.toPeriod))
    return Number(second.fromPeriod) <= first.toPeriod;

  return (
    Number(first.fromPeriod) <= second.toPeriod &&
    Number(second.fromPeriod) <= first.toPeriod
  );
}

function isAmountOverlapping(first: TChartSlab, second: TChartSlab): boolean {
  if (sameNumber(first.fromAmount, second.fromAmount)) return true;
  if (sameNumber(first.toAmount, second.toAmount)) return true;
  if (!isSet(first.toAmount)) return true;
  if (!isSet(second.toAmount))
    return Number(second.fromAmount) <= first.toAmount;

  return (
    Number(first.fromAmount) <= second.toAmount &&
    Number(second.fromAmount) <= first.toAmount
  );
}

function isOverlapping(
  first: TChartSlab,
  second: TChartSlab,
  byAmount: boolean,
): boolean {
  const amountOverlaps = isAmountOverlapping(first, second);
  const periodOverlaps = isPeriodOverlapping(first, second);
  const amountSame = isAmountSame(first, second);
  const periodSame = isPeriodSame(first, second);

  return byAmount
    ? (amountOverlaps && !amountSame) || (amountSame && periodOverlaps)
    : (periodOverlaps && !periodSame) || (periodSame && amountOverlaps);
}

function hasGap(
  first: TChartSlab,
  second: TChartSlab,
  byAmount: boolean,
): boolean {
  const amountSame = isAmountSame(first, second);
  const periodSame = isPeriodSame(first, second);
  const hasPeriods = isSet(first.fromPeriod) || isSet(second.fromPeriod);
  const hasAmounts = isSet(first.fromAmount) || isSet(second.fromAmount);

  if (byAmount) {
    // Same amount band, tiered by period within it.
    if (amountSame && hasPeriods) {
      return (
        !isSet(first.toPeriod) || periodDoesNotFollow(first, second.fromPeriod)
      );
    }

    if (amountSame) {
      return false;
    }

    // Crossing into a new amount band: the amounts must touch, and each band's
    // period sub-range must restart.
    return (
      amountDoesNotFollow(first, second.fromAmount) ||
      isNotProperPeriodStart(first) ||
      isNotProperPeriodStart(second)
    );
  }

  if (periodSame && hasAmounts) {
    return (
      !isSet(first.toAmount) || amountDoesNotFollow(first, second.fromAmount)
    );
  }

  if (periodSame) {
    return false;
  }

  // NOTE: the third term mirrors the backend exactly, including what looks
  // like a copy of the wrong axis — `IsNotProperPeriodStart(second)` where the
  // amount-primary branch above uses the *secondary* axis. It means an
  // ordinary period-primary chart (1-30, 31-90, 91-...) is rejected as having
  // gaps, because the second band's `fromPeriod` is 31 rather than 0 or 1.
  // Mirrored rather than corrected so the form refuses exactly what the server
  // refuses. See Q3 in docs/ADMIN_BACKEND_QUESTIONS.md.
  return (
    periodDoesNotFollow(first, second.fromPeriod) ||
    isNotProperAmountStart(first) ||
    isNotProperPeriodStart(second)
  );
}
